import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Workflow as WorkflowIcon, Search, Plus, LayoutGrid, List, Clock, Play, Check } from 'lucide-react'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { notifyAnnotatorBlocked } from '@/service/annotatorAccess'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'
import AppSidebar from '@/components/AppSidebar'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import {
  IntuiSelect,
  IntuiSelectTrigger,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import { IntuiTabs, IntuiTabsList, IntuiTabsTrigger } from '@/components/intueri/IntuiTabs'
import { IntuiEmptyState } from '@/components/intueri/IntuiEmptyState'
import {
  IntuiAlertDialog,
  IntuiAlertDialogAction,
  IntuiAlertDialogCancel,
  IntuiAlertDialogContent,
  IntuiAlertDialogDescription,
  IntuiAlertDialogFooter,
  IntuiAlertDialogHeader,
  IntuiAlertDialogTitle,
} from '@/components/intueri/IntuiAlertDialog'
import HelpAgentLauncherSlot from '@/components/HelpAgent/HelpAgentLauncherSlot'
import { PAGE_HEADER, PAGE_TITLE } from '@/components/layout/pageHeader'
import SquareLoader from '../SquareLoader'
import WorkflowCard from '../WorkflowCard'
import WorkflowTable from '../WorkflowTable'
import type { Workflow, WorkflowSortKey, WorkflowViewMode } from '../types'
import { api, unavailableOf, type Unavailable } from '../v4/api'
import type { RunListItem } from '../v4/contracts'
import RunNotificationsBell from './RunNotificationsBell'
import { kindOf, runToCard, selectRuns, type RunKind } from './runLibrary'
import { ERROR_BAR, HINT_BAR, isAvailabilityHint, unavailableText } from './unavailableText'

/** How often the list reloads, so a run that parks while the page is open shows up without a reload. */
const RELOAD_MS = 30_000

/** A single pill in the filter row (Figma "2. Workflow Selection"). */
function FilterChip({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'border-transparent bg-cyan-700 text-white hover:bg-cyan-800'
          : 'border-border bg-background text-foreground hover:bg-muted dark:bg-dark-card'
      )}
    >
      {icon}
      {children}
    </button>
  )
}

/**
 * The workflow library over v4 runs (the old "1. Workflow Entry Point" look): every run of the
 * organization as a card or a row, named by its question, with the engine's status as its line,
 * newest activity first. The chips split what waits on you, what the engine is working on and
 * what ended; Create Workflow opens the builder to ask a question; a card opens its run.
 */
export default function RunLibrary() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAppStore()
  // Annotators are read-only outside the P&ID editor: the click gives them the read-only toast.
  const isAnnotator = isAnnotatorRole(user?.role)
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState<RunKind>('all')
  const [sortBy, setSortBy] = useState<WorkflowSortKey>('last_used')
  const [viewMode, setViewMode] = useState<WorkflowViewMode>('grid')

  const [runs, setRuns] = useState<RunListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState<Unavailable | null>(null)
  const [error, setError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Workflow | null>(null)
  // Runs deleted on this page: a list fetched before the delete committed must not bring one back.
  const deleted = useRef(new Set<string>())

  const load = useCallback(async () => {
    try {
      const { runs: items } = await api.list()
      setRuns(items.filter((r) => !deleted.current.has(r.runId)))
      setUnavailable(null)
      setError('')
    } catch (e) {
      const u = unavailableOf(e)
      if (u) setUnavailable(u)
      else setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
    const interval = setInterval(() => void load(), RELOAD_MS)
    return () => clearInterval(interval)
  }, [load])

  const cards = useMemo(
    () => runs.filter((r) => kind === 'all' || kindOf(r) === kind).map((r) => runToCard(r, t)),
    [runs, kind, t]
  )
  const displayed = useMemo(() => selectRuns(cards, { search, sortBy }), [cards, search, sortBy])

  const handleCreate = () => {
    if (isAnnotator) return notifyAnnotatorBlocked()
    navigate('/workflow/new')
  }
  const handleOpen = (w: Workflow) => {
    if (isAnnotator) return notifyAnnotatorBlocked()
    navigate(`/workflow/new?runId=${encodeURIComponent(w.id)}`)
  }
  const handleViewReport = (w: Workflow) =>
    navigate(`/workflow/new?runId=${encodeURIComponent(w.id)}&view=report`)
  const handleDelete = async () => {
    if (!deleteTarget) return
    const runId = deleteTarget.id
    setDeleteTarget(null)
    try {
      await api.delete(runId)
      deleted.current.add(runId)
      setRuns((prev) => prev.filter((r) => r.runId !== runId))
    } catch {
      setError(t('workflow:row.delete_failed', { defaultValue: 'Could not delete workflow' }))
    }
  }
  const rowActions = { onOpen: handleOpen, onViewReport: handleViewReport, onDelete: setDeleteTarget }

  const chips: Array<[RunKind, React.ReactNode, string]> = [
    ['all', undefined, t('workflow:filter.all', { defaultValue: 'All Workflows' })],
    ['needs_you', <Clock key="c" className="size-3.5" />, t('workflow:filter.needs_you', { defaultValue: 'Needs you' })],
    ['running', <Play key="p" className="size-3.5" />, t('workflow:filter.running', { defaultValue: 'Running' })],
    ['finished', <Check key="f" className="size-3.5" />, t('workflow:filter.finished', { defaultValue: 'Finished' })],
  ]

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        <header className={`${PAGE_HEADER} justify-between`}>
          <div className="flex items-center">
            <span className={PAGE_TITLE}>{t('nav:menu.workflow', { defaultValue: 'Workflow' })}</span>
          </div>
          <div className="flex items-center gap-2">
            <RunNotificationsBell runs={runs} />
            <HelpAgentLauncherSlot />
          </div>
        </header>

        {unavailable && (
          <div
            role={isAvailabilityHint(unavailable) ? 'status' : 'alert'}
            className={isAvailabilityHint(unavailable) ? HINT_BAR : ERROR_BAR}
          >
            {unavailableText(unavailable, t)}
          </div>
        )}
        {error && (
          <div role="alert" className="border-b border-red-200 bg-red-50 px-6 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="flex flex-1 flex-col gap-6 overflow-hidden p-6">
          <div className="flex flex-col gap-4">
            <div className="flex w-full items-center justify-between gap-3">
              <div className="relative w-full sm:w-[280px]">
                <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <IntuiInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('workflow:search', { defaultValue: 'Search' })}
                  className="h-9 ps-9"
                />
              </div>
              {/* data-tour-id: help-agent "create-workflow" tour anchor. */}
              <IntuiButton size="lg" leftIcon={<Plus />} onClick={handleCreate} className="hidden shrink-0 sm:inline-flex" data-tour-id="workflow-create">
                {t('workflow:create', { defaultValue: 'Create Workflow' })}
              </IntuiButton>
              <IntuiButton size="icon-lg" onClick={handleCreate} aria-label={t('workflow:create', { defaultValue: 'Create Workflow' })} className="shrink-0 sm:hidden">
                <Plus className="size-4" />
              </IntuiButton>
            </div>

            <div className="flex w-full flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                {chips.map(([value, icon, label]) => (
                  <FilterChip key={value} active={kind === value} onClick={() => setKind(value)} icon={icon}>
                    {label}
                  </FilterChip>
                ))}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <IntuiSelect value={sortBy} onValueChange={(v) => setSortBy(v as WorkflowSortKey)}>
                  <IntuiSelectTrigger className="h-9 w-[144px]">
                    <IntuiSelectValue />
                  </IntuiSelectTrigger>
                  <IntuiSelectContent>
                    <IntuiSelectItem value="last_used">{t('workflow:sort.last_used', { defaultValue: 'Last used' })}</IntuiSelectItem>
                    <IntuiSelectItem value="name_az">{t('workflow:sort.name_az', { defaultValue: 'Name (A → Z)' })}</IntuiSelectItem>
                    <IntuiSelectItem value="name_za">{t('workflow:sort.name_za', { defaultValue: 'Name (Z → A)' })}</IntuiSelectItem>
                  </IntuiSelectContent>
                </IntuiSelect>
                <IntuiTabs value={viewMode} onValueChange={(v) => setViewMode(v as WorkflowViewMode)}>
                  <IntuiTabsList className="h-9">
                    <IntuiTabsTrigger value="grid" aria-label={t('workflow:view.grid', { defaultValue: 'Grid view' })}>
                      <LayoutGrid className="size-4" />
                    </IntuiTabsTrigger>
                    <IntuiTabsTrigger value="list" aria-label={t('workflow:view.list', { defaultValue: 'List view' })}>
                      <List className="size-4" />
                    </IntuiTabsTrigger>
                  </IntuiTabsList>
                </IntuiTabs>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3">
              <SquareLoader />
              <span className="text-sm text-muted-foreground">{t('workflow:loading', { defaultValue: 'Loading workflows...' })}</span>
            </div>
          ) : displayed.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <IntuiEmptyState
                icon={<WorkflowIcon className="size-4 text-muted-foreground" />}
                title={t('workflow:empty.title', { defaultValue: 'No workflow yet' })}
                description={t('workflow:empty.description_run', { defaultValue: 'Ask your first question to start a workflow' })}
                className="w-[308px]"
              />
            </div>
          ) : viewMode === 'grid' ? (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {displayed.map((w) => (
                  <WorkflowCard key={w.id} workflow={w} schedule={false} menuHidden={isAnnotator} {...rowActions} />
                ))}
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <WorkflowTable workflows={displayed} schedule={false} menuHidden={isAnnotator} {...rowActions} />
            </div>
          )}
        </div>
      </div>

      <IntuiAlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <IntuiAlertDialogContent>
          <IntuiAlertDialogHeader>
            <IntuiAlertDialogTitle>{t('workflow:row.delete_title', { defaultValue: 'Delete workflow' })}</IntuiAlertDialogTitle>
            <IntuiAlertDialogDescription>
              {t('workflow:row.delete_description', {
                name: deleteTarget?.name,
                defaultValue: 'Are you sure? "{{name}}" will be removed from your workflows.',
              })}
            </IntuiAlertDialogDescription>
          </IntuiAlertDialogHeader>
          <IntuiAlertDialogFooter>
            <IntuiAlertDialogCancel>{t('common:buttons.cancel', { defaultValue: 'Cancel' })}</IntuiAlertDialogCancel>
            <IntuiAlertDialogAction variant="destructive" onClick={() => void handleDelete()}>
              {t('workflow:row.delete', { defaultValue: 'Delete' })}
            </IntuiAlertDialogAction>
          </IntuiAlertDialogFooter>
        </IntuiAlertDialogContent>
      </IntuiAlertDialog>
    </div>
  )
}
